import { toast } from "@/utils/toast";
import { handleError } from "@/utils/error-handler";

export interface PeerConnection {
  connection: RTCPeerConnection;
  dataChannel: RTCDataChannel | null;
  remoteStream: MediaStream | null;
  localStream: MediaStream | null;
}

class WebRTCService {
  private peerConnections: Map<string, PeerConnection> = new Map();
  private localStream: MediaStream | null = null;
  private isInitialized = false;
  private onRemoteStreamCallback: ((stream: MediaStream, peerId: string) => void) | null = null;
  private onDataChannelMessageCallback: ((data: any, peerId: string) => void) | null = null;
  private onConnectionStateChangeCallback: ((state: RTCPeerConnectionState, peerId: string) => void) | null = null;
  private initializationPromise: Promise<MediaStream> | null = null;
  private iceServers: RTCIceServer[] = [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
    { urls: 'stun:stun2.l.google.com:19302' },
    { urls: 'stun:stun3.l.google.com:19302' },
    { urls: 'stun:stun4.l.google.com:19302' },
    {
      urls: 'turn:openrelay.metered.ca:80',
      username: 'openrelayproject',
      credential: 'openrelayproject'
    }
  ];
  private isMockConnection: boolean = false;
  private mediaDeviceErrorHandled: boolean = false;

  public async initialize(
    videoConstraints: MediaStreamConstraints = { video: true, audio: true }
  ): Promise<MediaStream> {
    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    if (this.isInitialized && this.localStream) {
      return this.localStream;
    }

    this.initializationPromise = (async () => {
      try {
        console.log("Initializing WebRTC service with constraints:", videoConstraints);
        
        this.localStream = await navigator.mediaDevices.getUserMedia(videoConstraints);
        this.isInitialized = true;
        this.mediaDeviceErrorHandled = false;
        
        console.log("WebRTC service initialized successfully:", 
          this.localStream.getTracks().map(t => `${t.kind}: ${t.label} (enabled: ${t.enabled})`));
        
        return this.localStream;
      } catch (error) {
        console.error("Failed to initialize WebRTC service:", error);
        
        // Handle permission denied case
        if (error instanceof DOMException && 
            (error.name === "NotAllowedError" || error.name === "PermissionDeniedError")) {
          
          if (!this.mediaDeviceErrorHandled) {
            toast.error("Camera or microphone access denied. Please check your permissions.");
            this.mediaDeviceErrorHandled = true;
          }
          
          // Create an empty stream as fallback
          const emptyStream = new MediaStream();
          this.localStream = emptyStream;
          this.isInitialized = true;
          
          return emptyStream;
        }
        
        // Try with audio only if video failed
        if (videoConstraints.video && !this.mediaDeviceErrorHandled) {
          console.log("Trying to initialize with audio only...");
          this.initializationPromise = null;
          this.mediaDeviceErrorHandled = true;
          return this.initialize({ video: false, audio: true });
        }
        
        this.initializationPromise = null;
        throw error;
      }
    })();

    return this.initializationPromise;
  }

  public async enableCamera(): Promise<boolean> {
    try {
      if (!this.localStream) {
        await this.initialize();
        return true;
      }
      
      // Check if we already have video tracks
      const hasVideoTracks = this.localStream.getVideoTracks().length > 0;
      if (hasVideoTracks) {
        return true;
      }
      
      // Try to get camera access
      const videoStream = await navigator.mediaDevices.getUserMedia({ video: true });
      const videoTrack = videoStream.getVideoTracks()[0];
      
      if (videoTrack) {
        this.localStream.addTrack(videoTrack);
        
        // Add the track to all peer connections
        for (const [peerId, peerConnection] of this.peerConnections.entries()) {
          try {
            peerConnection.connection.addTrack(videoTrack, this.localStream);
          } catch (e) {
            console.error(`Failed to add video track to peer ${peerId}:`, e);
          }
        }
        
        return true;
      }
      
      return false;
    } catch (error) {
      console.error("Failed to enable camera:", error);
      return false;
    }
  }

  public async createPeerConnection(
    peerId: string,
    initiator = false
  ): Promise<PeerConnection> {
    if (!this.isInitialized) {
      try {
        await this.initialize();
      } catch (error) {
        console.error("Failed to initialize before creating peer connection:", error);
        throw new Error("WebRTC service initialization failed");
      }
    }

    if (this.peerConnections.has(peerId)) {
      await this.closePeerConnection(peerId);
    }

    const connection = new RTCPeerConnection({
      iceServers: this.iceServers,
      iceCandidatePoolSize: 10
    });

    const dataChannel = initiator
      ? connection.createDataChannel("chat", {
          ordered: true,
        })
      : null;

    if (dataChannel) {
      this.setupDataChannel(dataChannel, peerId);
    }

    connection.onicecandidate = (event) => {
      if (event.candidate) {
        console.log("New ICE candidate:", event.candidate);
      }
    };

    connection.onconnectionstatechange = () => {
      console.log(`Connection state changed: ${connection.connectionState} for peer ${peerId}`);
      
      if (this.onConnectionStateChangeCallback) {
        this.onConnectionStateChangeCallback(connection.connectionState, peerId);
      }
      
      if (connection.connectionState === 'connected') {
        toast.success("Connected to peer");
      } else if (connection.connectionState === 'disconnected' || connection.connectionState === 'failed') {
        toast.error("Connection to peer lost");
      }
    };

    connection.ondatachannel = (event) => {
      const incomingDataChannel = event.channel;
      this.setupDataChannel(incomingDataChannel, peerId);
    };

    connection.ontrack = (event) => {
      console.log("Received remote tracks:", event.streams);
      if (event.streams && event.streams[0]) {
        const remoteStream = event.streams[0];
        
        const peerConnection = this.peerConnections.get(peerId);
        if (peerConnection) {
          peerConnection.remoteStream = remoteStream;
          this.peerConnections.set(peerId, peerConnection);
        }
        
        if (this.onRemoteStreamCallback) {
          this.onRemoteStreamCallback(remoteStream, peerId);
        }
      }
    };

    if (this.localStream) {
      this.localStream.getTracks().forEach((track) => {
        try {
          if (this.localStream) {
            connection.addTrack(track, this.localStream);
          }
        } catch (e) {
          console.error(`Failed to add track ${track.kind} to peer ${peerId}:`, e);
        }
      });
    }

    const peerConnection: PeerConnection = {
      connection,
      dataChannel,
      remoteStream: null,
      localStream: this.localStream
    };

    this.peerConnections.set(peerId, peerConnection);
    return peerConnection;
  }

  private setupDataChannel(dataChannel: RTCDataChannel, peerId: string): void {
    dataChannel.onopen = () => {
      console.log(`Data channel is open for peer ${peerId}`);
    };

    dataChannel.onclose = () => {
      console.log(`Data channel is closed for peer ${peerId}`);
    };

    dataChannel.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log(`Received data from ${peerId}:`, data);
        
        if (this.onDataChannelMessageCallback) {
          this.onDataChannelMessageCallback(data, peerId);
        }
      } catch (error) {
        console.error(`Error parsing data from ${peerId}:`, error);
      }
    };

    const peerConnection = this.peerConnections.get(peerId);
    if (peerConnection) {
      peerConnection.dataChannel = dataChannel;
      this.peerConnections.set(peerId, peerConnection);
    }
  }

  public async createOffer(peerId: string): Promise<RTCSessionDescriptionInit> {
    const peerConnection = this.peerConnections.get(peerId);
    
    if (!peerConnection) {
      throw new Error(`No peer connection for ${peerId}`);
    }
    
    try {
      const offer = await peerConnection.connection.createOffer({
        offerToReceiveAudio: true,
        offerToReceiveVideo: true
      });
      
      await peerConnection.connection.setLocalDescription(offer);
      return offer;
    } catch (error) {
      console.error(`Error creating offer for ${peerId}:`, error);
      throw error;
    }
  }

  public async createAnswer(
    peerId: string,
    offer: RTCSessionDescriptionInit
  ): Promise<RTCSessionDescriptionInit> {
    const peerConnection = this.peerConnections.get(peerId);
    
    if (!peerConnection) {
      throw new Error(`No peer connection for ${peerId}`);
    }
    
    try {
      await peerConnection.connection.setRemoteDescription(new RTCSessionDescription(offer));
      const answer = await peerConnection.connection.createAnswer();
      await peerConnection.connection.setLocalDescription(answer);
      return answer;
    } catch (error) {
      console.error(`Error creating answer for ${peerId}:`, error);
      throw error;
    }
  }

  public async setRemoteDescription(
    peerId: string,
    description: RTCSessionDescriptionInit
  ): Promise<void> {
    const peerConnection = this.peerConnections.get(peerId);
    
    if (!peerConnection) {
      throw new Error(`No peer connection for ${peerId}`);
    }
    
    try {
      await peerConnection.connection.setRemoteDescription(
        new RTCSessionDescription(description)
      );
    } catch (error) {
      console.error(`Error setting remote description for ${peerId}:`, error);
      throw error;
    }
  }

  public async addIceCandidate(
    peerId: string,
    candidate: RTCIceCandidateInit
  ): Promise<void> {
    const peerConnection = this.peerConnections.get(peerId);
    
    if (!peerConnection) {
      throw new Error(`No peer connection for ${peerId}`);
    }
    
    try {
      await peerConnection.connection.addIceCandidate(new RTCIceCandidate(candidate));
    } catch (error) {
      console.error(`Error adding ICE candidate for ${peerId}:`, error);
      throw error;
    }
  }

  public async closePeerConnection(peerId: string): Promise<void> {
    const peerConnection = this.peerConnections.get(peerId);
    
    if (!peerConnection) {
      return;
    }
    
    try {
      if (peerConnection.dataChannel) {
        peerConnection.dataChannel.close();
      }
      
      peerConnection.connection.close();
    } catch (error) {
      console.error(`Error closing peer connection ${peerId}:`, error);
    }
    
    this.peerConnections.delete(peerId);
  }

  public async dispose(): Promise<void> {
    console.log("Disposing WebRTC service");
    
    for (const peerId of this.peerConnections.keys()) {
      await this.closePeerConnection(peerId);
    }
    
    if (this.localStream) {
      console.log("Stopping all local tracks");
      this.localStream.getTracks().forEach((track) => {
        console.log(`Stopping track: ${track.kind} (${track.label})`);
        track.stop();
      });
      this.localStream = null;
    }
    
    this.isInitialized = false;
    this.initializationPromise = null;
    this.mediaDeviceErrorHandled = false;
    console.log("WebRTC service disposed");
  }

  public sendMessage(peerId: string, message: any): boolean {
    const peerConnection = this.peerConnections.get(peerId);
    
    if (!peerConnection || !peerConnection.dataChannel || peerConnection.dataChannel.readyState !== "open") {
      return false;
    }
    
    try {
      peerConnection.dataChannel.send(JSON.stringify(message));
      return true;
    } catch (error) {
      console.error(`Error sending message to ${peerId}:`, error);
      return false;
    }
  }

  public getConnectionState(peerId: string): RTCPeerConnectionState | null {
    const peerConnection = this.peerConnections.get(peerId);
    return peerConnection ? peerConnection.connection.connectionState : null;
  }

  public getLocalStream(): MediaStream | null {
    return this.localStream;
  }

  public getRemoteStream(peerId: string): MediaStream | null {
    const peerConnection = this.peerConnections.get(peerId);
    return peerConnection ? peerConnection.remoteStream : null;
  }

  public onRemoteStream(callback: (stream: MediaStream, peerId: string) => void): void {
    this.onRemoteStreamCallback = callback;
  }

  public onDataChannelMessage(callback: (data: any, peerId: string) => void): void {
    this.onDataChannelMessageCallback = callback;
  }

  public onConnectionStateChange(callback: (state: RTCPeerConnectionState, peerId: string) => void): void {
    this.onConnectionStateChangeCallback = callback;
  }

  public async mockConnection(remoteVideoUrl: string): Promise<string> {
    try {
      const mockPeerId = 'mock-peer-' + Date.now();
      await this.createPeerConnection(mockPeerId, true);
      this.isMockConnection = true;
      
      // Create a dummy remote stream for testing
      this.createDummyStream(mockPeerId);
      
      return mockPeerId;
    } catch (error) {
      console.error("Error in mockConnection:", error);
      handleError(error, { 
        customMessage: "Failed to establish mock connection",
        showToast: true
      });
      throw error;
    }
  }

  private createSilentAudioTrack(): MediaStreamTrack | null {
    try {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      oscillator.frequency.value = 0; // Silent
      
      const destination = audioContext.createMediaStreamDestination();
      oscillator.connect(destination);
      oscillator.start();
      
      const tracks = destination.stream.getAudioTracks();
      return tracks.length > 0 ? tracks[0] : null;
    } catch (error) {
      console.error("Error creating silent audio track:", error);
      return null;
    }
  }

  private async createDummyStream(peerId: string): Promise<void> {
    try {
      const canvas = document.createElement('canvas');
      canvas.width = 640;
      canvas.height = 480;
      
      const ctx = canvas.getContext('2d');
      if (ctx) {
        // Create search animation with context
        ctx.fillStyle = '#1C1F37';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        
        ctx.font = '30px Arial';
        ctx.fillStyle = 'white';
        ctx.textAlign = 'center';
        ctx.fillText('Searching for partner...', canvas.width/2, canvas.height/2 - 20);
        ctx.font = '20px Arial';
        ctx.fillText('Please wait', canvas.width/2, canvas.height/2 + 20);
        
        // Animation interval for dots
        setInterval(() => {
          if (!ctx) return;
          
          ctx.fillStyle = '#1C1F37';
          ctx.fillRect(0, canvas.height/2 + 30, canvas.width, 40);
          
          const now = Date.now();
          const numDots = Math.floor((now % 1000) / 250) + 1;
          
          ctx.fillStyle = 'white';
          ctx.textAlign = 'center';
          ctx.font = '30px Arial';
          ctx.fillText('.'.repeat(numDots), canvas.width/2, canvas.height/2 + 60);
        }, 250);
        
        // Use browser-compatible method to capture stream
        let dummyStream: MediaStream | null = null;
        
        if (canvas.captureStream) {
          dummyStream = canvas.captureStream(30);
        } else if ((canvas as any).mozCaptureStream) {
          dummyStream = (canvas as any).mozCaptureStream(30);
        } else {
          console.warn("Canvas stream capture not supported in this browser");
          // Create an empty stream as fallback
          dummyStream = new MediaStream();
        }
        
        if (dummyStream) {
          // Add an audio track to the dummy stream
          const silentAudioTrack = this.createSilentAudioTrack();
          if (silentAudioTrack) {
            dummyStream.addTrack(silentAudioTrack);
          }
          
          const peerConnection = this.peerConnections.get(peerId);
          if (peerConnection) {
            peerConnection.remoteStream = dummyStream;
            this.peerConnections.set(peerId, peerConnection);
            
            if (this.onRemoteStreamCallback) {
              this.onRemoteStreamCallback(dummyStream, peerId);
            }
          }
        }
      }
    } catch (error) {
      console.error("Error creating dummy stream:", error);
    }
  }
  
  public isMockConnectionActive(): boolean {
    return this.isMockConnection;
  }
  
  public toggleVideoTrack(enable: boolean): boolean {
    if (!this.localStream) return false;
    
    const videoTracks = this.localStream.getVideoTracks();
    console.log(`Toggling video tracks (${videoTracks.length}) to ${enable ? 'enabled' : 'disabled'}`);
    
    if (videoTracks.length === 0) {
      if (enable) {
        // Try to get video track if we don't have one but want to enable it
        this.enableCamera().catch(err => {
          console.error("Failed to enable camera:", err);
        });
      }
      return false;
    }
    
    videoTracks.forEach(track => {
      track.enabled = enable;
      console.log(`Video track ${track.label} is now ${track.enabled ? 'enabled' : 'disabled'}`);
    });
    
    return true;
  }
  
  public toggleAudioTrack(enable: boolean): boolean {
    if (!this.localStream) return false;
    
    const audioTracks = this.localStream.getAudioTracks();
    console.log(`Toggling audio tracks (${audioTracks.length}) to ${enable ? 'enabled' : 'disabled'}`);
    
    if (audioTracks.length === 0) return false;
    
    audioTracks.forEach(track => {
      track.enabled = enable;
      console.log(`Audio track ${track.label} is now ${track.enabled ? 'enabled' : 'disabled'}`);
    });
    
    return true;
  }
  
  public isVideoEnabled(): boolean {
    if (!this.localStream) return false;
    
    const videoTracks = this.localStream.getVideoTracks();
    if (videoTracks.length === 0) return false;
    
    // Return true if at least one video track is enabled
    return videoTracks.some(track => track.enabled);
  }
  
  public isAudioEnabled(): boolean {
    if (!this.localStream) return false;
    
    const audioTracks = this.localStream.getAudioTracks();
    if (audioTracks.length === 0) return false;
    
    // Return true if at least one audio track is enabled
    return audioTracks.some(track => track.enabled);
  }
}

export const webRTCService = new WebRTCService();
export default webRTCService;
